import React, { useState, useEffect } from 'react';
import {
    Card, CardContent, Typography, List, ListItem, ListItemText,
    IconButton, Chip, Box, Button, Alert, CircularProgress, Divider, Grid,
    Avatar, alpha, useTheme
} from '@mui/material';
import { Build, CheckCircle, PriorityHigh, Warning, Visibility, EditCalendar } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useAxiosPrivate from '../hooks/useAxiosPrivate';
import { useTranslation } from 'react-i18next';

const MaintenanceWidget = () => {
    const { t } = useTranslation('global');
    const axiosPrivate = useAxiosPrivate();
    const navigate = useNavigate();
    const theme = useTheme();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadNotifications();
    }, []);

    const loadNotifications = async () => {
        try {
            setLoading(true);
            const response = await axiosPrivate.get('/maintenance/notifications');
            setNotifications(response.data);
        } catch (err) {
            console.error('Errore nel caricamento delle notifiche manutenzione:', err);
            setError('Impossibile caricare le notifiche');
        } finally {
            setLoading(false);
        }
    };

    const handleDismiss = async (notificationId) => {
        try {
            await axiosPrivate.put(`/maintenance/notifications/${notificationId}/dismiss`);
            // Rimuovi la notifica dalla lista
            setNotifications(notifications.filter(n => n.notification_id !== notificationId));
        } catch (err) {
            console.error('Errore nella dismissione della notifica:', err);
            alert('Errore nell\'aggiornamento della notifica');
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'urgente': return '#d32f2f';
            case 'alta': return '#f57c00';
            case 'media': return '#fbc02d';
            case 'bassa': return '#388e3c';
            default: return '#9e9e9e';
        }
    };

    const getPriorityIcon = (priority) => {
        switch (priority) {
            case 'urgente': return <PriorityHigh sx={{ color: 'error.main' }} />;
            case 'alta': return <Warning sx={{ color: 'warning.dark' }} />;
            default: return <Build />;
        }
    };

    const categoryIcons = {
        idraulica: '💧',
        elettrica: '⚡',
        riscaldamento: '🔥',
        climatizzazione: '❄️',
        serramenti: '🚪',
        tinteggiatura: '🎨',
        giardino: '🌳',
        attrezzature_mediche: '🏥',
        pulizie: '🧹',
        disinfestazione: '🐛',
        altro: '🔧'
    };

    if (loading) {
        return (
            <Card sx={{ 
                p: { xs: 2, sm: 3 }, 
                height: '100%', 
                minHeight: 400, 
                display: 'flex', 
                flexDirection: 'column', 
                borderTop: `4px solid ${theme.palette.primary.main}` 
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Avatar sx={{ 
                        bgcolor: alpha(theme.palette.primary.main, 0.15), 
                        width: { xs: 32, sm: 36 }, 
                        height: { xs: 32, sm: 36 } 
                    }}>
                        <Build sx={{ color: theme.palette.primary.main, fontSize: { xs: 18, sm: 20 } }} />
                    </Avatar>
                    <Typography 
                        variant="h6" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '0.95rem', sm: '1.15rem' } }}
                    >
                        {t('dashboard.maintenance.title', 'Manutenzioni in Arrivo')}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                    <CircularProgress size={40} />
                </Box>
            </Card>
        );
    }

    if (error) {
        return (
            <Card sx={{ 
                p: { xs: 2, sm: 3 }, 
                height: '100%', 
                minHeight: 400, 
                display: 'flex', 
                flexDirection: 'column', 
                borderTop: `4px solid ${theme.palette.primary.main}` 
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Avatar sx={{ 
                        bgcolor: alpha(theme.palette.primary.main, 0.15), 
                        width: { xs: 32, sm: 36 }, 
                        height: { xs: 32, sm: 36 } 
                    }}>
                        <Build sx={{ color: theme.palette.primary.main, fontSize: { xs: 18, sm: 20 } }} />
                    </Avatar>
                    <Typography 
                        variant="h6" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '0.95rem', sm: '1.15rem' } }}
                    >
                        {t('dashboard.maintenance.title', 'Manutenzioni in Arrivo')}
                    </Typography>
                </Box>
                <Alert severity="error">{error}</Alert>
            </Card>
        );
    }

    // Limita a massimo 3 notifiche
    const displayedNotifications = notifications.slice(0, 3);

    return (
        <Card sx={{ 
            p: { xs: 2, sm: 3 }, 
            height: '100%', 
            minHeight: 400, 
            display: 'flex', 
            flexDirection: 'column', 
            borderTop: `4px solid ${theme.palette.primary.main}` 
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                    <Avatar sx={{ 
                        bgcolor: alpha(theme.palette.primary.main, 0.15), 
                        width: { xs: 32, sm: 36 }, 
                        height: { xs: 32, sm: 36 },
                        flexShrink: 0
                    }}>
                        <Build sx={{ color: theme.palette.primary.main, fontSize: { xs: 18, sm: 20 } }} />
                    </Avatar>
                    <Typography 
                        variant="h6" 
                        fontWeight="bold"
                        sx={{ 
                            fontSize: { xs: '0.95rem', sm: '1.15rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {t('dashboard.maintenance.title', 'Manutenzioni in Arrivo')}
                    </Typography>
                </Box>
                <Chip
                    label={notifications.length}
                    size="small"
                    color="primary"
                    sx={{ fontWeight: 700, flexShrink: 0 }}
                />
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto' }}>
                {displayedNotifications.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Avatar sx={{ width: { xs: 56, sm: 64 }, height: { xs: 56, sm: 64 }, bgcolor: alpha('#9e9e9e', 0.1), mx: 'auto', mb: 2 }}>
                            <Build sx={{ fontSize: { xs: 28, sm: 32 }, color: 'text.disabled' }} />
                        </Avatar>
                        <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                        >
                            {t('dashboard.maintenance.noPending', 'Nessuna manutenzione programmata')}
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                        {displayedNotifications.map((notification) => (
                            <Box
                                key={notification.notification_id}
                                sx={{
                                    mb: 2,
                                    p: { xs: 1.5, sm: 2 },
                                    borderRadius: 2,
                                    backgroundColor: alpha(getPriorityColor(notification.priority), 0.08),
                                    borderLeft: `4px solid ${getPriorityColor(notification.priority)}`,
                                    cursor: 'pointer',
                                    '&:hover': { 
                                        backgroundColor: alpha(getPriorityColor(notification.priority), 0.15),
                                        transform: 'translateX(4px)'
                                    },
                                    transition: 'all 0.2s ease'
                                }}
                                onClick={() => navigate(`${process.env.REACT_APP_BASE_PATH || '/dashboard'}/maintenance`)}
                            >
                                <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 }, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                                    <Box sx={{ minWidth: { xs: 60, sm: 70 }, flexShrink: 0 }}>
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                color: getPriorityColor(notification.priority), 
                                                fontWeight: 'bold',
                                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                            }}
                                        >
                                            {t('dashboard.maintenance.tomorrow', 'Domani')}
                                        </Typography>
                                        {notification.scheduled_time && (
                                            <Typography 
                                                variant="caption" 
                                                color="text.secondary"
                                                sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                            >
                                                {notification.scheduled_time.substring(0, 5)}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography 
                                            variant="body2" 
                                            fontWeight="medium" 
                                            sx={{
                                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {categoryIcons[notification.category]} {notification.title}
                                        </Typography>
                                        <Chip 
                                            label={t(`dashboard.maintenance.priority.${notification.priority}`, notification.priority.charAt(0).toUpperCase() + notification.priority.slice(1))}
                                            size="small"
                                            sx={{ 
                                                mt: 0.5,
                                                height: { xs: 18, sm: 20 },
                                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                                bgcolor: alpha(getPriorityColor(notification.priority), 0.2),
                                                color: getPriorityColor(notification.priority)
                                            }}
                                        />
                                        {notification.location && (
                                            <Typography 
                                                variant="caption" 
                                                display="block" 
                                                color="text.secondary" 
                                                sx={{ 
                                                    mt: 0.5,
                                                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                📍 {notification.location}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
            
            <Button
                fullWidth
                variant="contained"
                startIcon={<Build />}
                onClick={() => navigate(`${process.env.REACT_APP_BASE_PATH || '/dashboard'}/maintenance`)}
                sx={{ 
                    mt: 2,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    py: { xs: 0.75, sm: 1 }
                }}
            >
                {displayedNotifications.length > 0 ? t('dashboard.maintenance.viewAll', 'Vedi tutte') : t('dashboard.maintenance.createMaintenance', 'Crea Manutenzione')}
            </Button>
        </Card>
    );
};

export default MaintenanceWidget;
